import React, { useEffect, useId, useState } from 'react';

export interface SectionSubnavItem {
  key: string;
  label: React.ReactNode;
  shortLabel?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

interface Props {
  activeKey: string;
  onChange: (key: string) => void;
  items: SectionSubnavItem[];
  isMobile?: boolean;
  ariaLabel?: string;
}

const SectionSubnav: React.FC<Props> = ({
  activeKey,
  onChange,
  items,
  isMobile = false,
  ariaLabel = 'Section navigation',
}) => {
  const baseId = useId();
  const activeItem = items.find((item) => item.key === activeKey) ?? items[0];
  const [mountedKeys, setMountedKeys] = useState<Set<string>>(
    () => new Set([activeKey])
  );

  useEffect(() => {
    setMountedKeys((current) => {
      if (current.has(activeKey)) {
        return current;
      }
      const next = new Set(current);
      next.add(activeKey);
      return next;
    });
  }, [activeKey]);

  return (
    <section className="ac-section-subnav" aria-label={ariaLabel}>
      <div className="ac-section-subnav__toolbar">
        <div className="ac-section-subnav__track" role="tablist" aria-label={ariaLabel}>
          {items.map((item) => {
            const selected = item.key === activeKey;
            const displayLabel =
              isMobile && item.shortLabel !== undefined ? item.shortLabel : item.label;

            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                id={`${baseId}-tab-${item.key}`}
                aria-selected={selected}
                aria-controls={`${baseId}-panel-${item.key}`}
                tabIndex={selected ? 0 : -1}
                className={`ac-section-subnav__tab${selected ? ' ac-section-subnav__tab--active' : ''}`}
                onClick={() => onChange(item.key)}
              >
                {item.icon ? (
                  <span className="ac-section-subnav__icon" aria-hidden>
                    {item.icon}
                  </span>
                ) : null}
                <span className="ac-section-subnav__label">{displayLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="ac-section-subnav__content"
        role="tabpanel"
        id={`${baseId}-panel-${activeItem.key}`}
        aria-labelledby={`${baseId}-tab-${activeItem.key}`}
      >
        {items.map((item) =>
          mountedKeys.has(item.key) ? (
            <div
              key={item.key}
              hidden={item.key !== activeKey}
              className="ac-section-subnav__panel"
            >
              {item.children}
            </div>
          ) : null
        )}
      </div>
    </section>
  );
};

export default SectionSubnav;
